import React, { useState, useEffect } from "react";
import styled from "styled-components";

const FormContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto;
`;

const FormTitle = styled.h2`
  color: #333;
  margin-bottom: 24px;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Button = styled.button`
  background: ${(props) => (props.primary ? "#007bff" : "#6c757d")};
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-right: 12px;

  &:hover {
    background: ${(props) => (props.primary ? "#0056b3" : "#545b62")};
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Alert = styled.div`
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  background: ${(props) => (props.type === "success" ? "#d4edda" : "#f8d7da")};
  color: ${(props) => (props.type === "success" ? "#155724" : "#721c24")};
  border: 1px solid
    ${(props) => (props.type === "success" ? "#c3e6cb" : "#f5c6cb")};
`;

const FeedbackRequestForm = ({
  onSubmit,
  onCancel,
  loading = false,
  prePopulatedData = null,
  title = "Request General Experience Feedback",
}) => {
  const [formData, setFormData] = useState({
    customerId: "",
    phone: "",
    customerName: "",
    referenceId: "",
    referenceType: "",
    serviceDetails: "",
    adminNotes: "",
  });

  const [alert, setAlert] = useState(null);

  // Pre-populate form when prePopulatedData changes
  useEffect(() => {
    if (prePopulatedData) {
      setFormData({
        customerId: prePopulatedData.customerId || "",
        phone: prePopulatedData.phone || "",
        customerName: prePopulatedData.customerName || "",
        referenceId: prePopulatedData.referenceId || "",
        referenceType: prePopulatedData.referenceType || "",
        serviceDetails: prePopulatedData.serviceDetails || "",
        adminNotes: prePopulatedData.adminNotes || "",
      });
    }
  }, [prePopulatedData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.customerId || !formData.phone) {
      setAlert({
        type: "error",
        message: "Please fill in all required fields",
      });
      return;
    }

    try {
      await onSubmit(formData);
      setAlert({
        type: "success",
        message: "Feedback request created successfully!",
      });
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          customerId: "",
          phone: "",
          customerName: "",
          referenceId: "",
          referenceType: "",
          serviceDetails: "",
          adminNotes: "",
        });
        setAlert(null);
      }, 2000);
    } catch (error) {
      setAlert({
        type: "error",
        message: error.message || "Failed to create feedback request",
      });
    }
  };

  return (
    <FormContainer>
      <FormTitle>{title}</FormTitle>

      {alert && <Alert type={alert.type}>{alert.message}</Alert>}

      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="customerId">Customer ID *</Label>
          <Input
            type="text"
            id="customerId"
            name="customerId"
            value={formData.customerId}
            onChange={handleChange}
            placeholder="Enter customer ID"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+27123456789"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="customerName">Customer Name</Label>
          <Input
            type="text"
            id="customerName"
            name="customerName"
            value={formData.customerName}
            onChange={handleChange}
            placeholder="Enter customer name"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="referenceId">Reference ID</Label>
          <Input
            type="text"
            id="referenceId"
            name="referenceId"
            value={formData.referenceId}
            onChange={handleChange}
            placeholder="Enter reference ID (optional)"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="referenceType">Reference Type</Label>
          <Select
            id="referenceType"
            name="referenceType"
            value={formData.referenceType}
            onChange={handleChange}
          >
            <option value="">Select reference type (optional)</option>
            <option value="lead">Lead</option>
            <option value="quote">Quote</option>
            <option value="order">Order</option>
            <option value="service">Service</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="serviceDetails">Service Details</Label>
          <TextArea
            id="serviceDetails"
            name="serviceDetails"
            value={formData.serviceDetails}
            onChange={handleChange}
            placeholder="Describe the service provided (optional)"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="adminNotes">Admin Notes</Label>
          <TextArea
            id="adminNotes"
            name="adminNotes"
            value={formData.adminNotes}
            onChange={handleChange}
            placeholder="Any additional notes (optional)"
          />
        </FormGroup>

        <ButtonGroup>
          <Button type="button" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" primary disabled={loading}>
            {loading ? "Creating..." : "Create Feedback Request"}
          </Button>
        </ButtonGroup>
      </form>
    </FormContainer>
  );
};

export default FeedbackRequestForm;
